// Funnel Summary rows and the per-row daily chart.
//
// A Funnel Summary row is a segment: deal type × region × language. The table
// sums the page's RPC rows that fall in the segment and pass the page filters.
// The row chart sums get_funnel_daily rows through the same two predicates, day
// by day. Both predicates live here, so a row's chart ends on the row's numbers.

export const normalizeLang = (lang: string | null | undefined): string => {
    const l = (lang || 'unknown').toLowerCase();
    if (l === 'english' || l === 'en') return 'English';
    if (l === 'french' || l === 'fr') return 'French';
    return 'Unknown';
};

// ---------------------------------------------------------------------------
// Page filters
// ---------------------------------------------------------------------------

export type SourceFilter = 'all' | 'organic' | 'ad' | 'maps';

/**
 * Does a leads.source_channel value fall under a Source filter? 'maps' covers
 * both Google Business Profile channels ('maps_gbp_montreal' / 'maps_gbp_toronto');
 * every other filter is an exact match.
 */
export const channelMatchesSource = (channel: string | null | undefined, source: SourceFilter): boolean => {
    const ch = channel || 'unknown';
    if (source === 'all') return true;
    if (source === 'maps') return ch.startsWith('maps_gbp');
    return ch === source;
};

const CHANNEL_LABELS: Record<string, string> = {
    ad: 'Ads',
    organic: 'Organic',
    maps_gbp_montreal: 'Google Maps (Montreal)',
    maps_gbp_toronto: 'Google Maps (Toronto)',
    sms: 'SMS',
};

/** Display label for a leads.source_channel value. */
export const channelLabel = (channel: string): string =>
    CHANNEL_LABELS[channel] ?? channel.charAt(0).toUpperCase() + channel.slice(1);

/** Timeline values match quotes.timeline; source values match leads.source_channel. */
export interface PageFilters {
    timeline: string;
    source: SourceFilter;
}

/**
 * The page-wide Timeline + Source predicate. 'all' on either dimension matches
 * every bucket (timeline includes rows with no timeline); otherwise an exact
 * match. Per the 20260525180000 / 20260617120000 migrations, no lead carries
 * more than one distinct timeline or source_channel, so the buckets sum to the
 * unfiltered totals.
 */
export const passesPageFilters = (
    r: { timeline?: string | null; source_channel?: string | null },
    f: PageFilters,
): boolean =>
    (f.timeline === 'all' || r.timeline === f.timeline) &&
    channelMatchesSource(r.source_channel, f.source);

// ---------------------------------------------------------------------------
// Segments
// ---------------------------------------------------------------------------

export type DealType = 'at_need' | 'pre_need';
export type Region = 'quebec' | 'ontario';

export interface FunnelSegment {
    /** null is the Grand Total: at-need plus pre-need. */
    dealType: DealType | null;
    region: Region | null;
    /** Set on the Quebec rows, which the table reads from get_funnel_by_language. */
    lang: 'French' | 'English' | null;
}

/** The Grand Total's segment: every deal type, region and language. */
const GRAND_TOTAL: FunnelSegment = { dealType: null, region: null, lang: null };

const inDealType = (dealType: string | null | undefined, seg: FunnelSegment) =>
    seg.dealType === null
        ? dealType === 'at_need' || dealType === 'pre_need'
        : dealType === seg.dealType;

// ---------------------------------------------------------------------------
// Table rows
// ---------------------------------------------------------------------------

/** A signed funeral, with its quotes (PostgREST embed) for the Source and Timeline filters. */
export interface WonTotalRow {
    contracts_signed_at: string;
    region: string | null;
    has_preneed: boolean | null;
    has_atneed: boolean | null;
    language: string | null;
    quotes?: {
        timeline?: string | null;
        leads?: { source_channel: string | null } | { source_channel: string | null }[] | null;
    }[] | null;
}

/**
 * The source channels of the leads behind a signed funeral. A funeral with no
 * lead (walk-in, entered by hand) has none: it counts under Source = All and
 * drops out under organic/ads, the same way an 'unknown' lead does elsewhere.
 */
const wonTotalSources = (r: WonTotalRow): string[] => {
    const out: string[] = [];
    (r.quotes || []).forEach((q) => {
        const leads = !q || !q.leads ? [] : Array.isArray(q.leads) ? q.leads : [q.leads];
        leads.forEach((l) => { if (l) out.push(l.source_channel || 'unknown'); });
    });
    return out;
};

/**
 * The timelines on a signed funeral's quotes. A funeral has no timeline of its
 * own: this is the stage captured on the quote, before the case was won, so a
 * case quoted as "near future" that then became at-need still reads that way.
 * Cases whose quote never recorded a stage have none and drop out of any
 * Timeline filter, which is why the stages do not sum to the All view.
 */
const wonTotalTimelines = (r: WonTotalRow): string[] => {
    const out: string[] = [];
    (r.quotes || []).forEach((q) => { if (q && q.timeline) out.push(q.timeline); });
    return out;
};

/**
 * Won Total counts signed funerals, not leads. The Grand Total counts each
 * funeral once.
 *
 * `applyTimeline` is the difference between the column and the chart. The
 * column counts every contract signed in the window and follows Source only.
 * The chart's line follows Timeline as well, so that a filtered chart does not
 * mix a filtered series with an unfiltered one; under a Timeline filter the
 * line is therefore lower than its column.
 */
export const wonTotalInSegment = (
    r: WonTotalRow,
    seg: FunnelSegment,
    filters: PageFilters,
    applyTimeline = false,
): boolean => {
    if (seg.dealType === 'pre_need' && !r.has_preneed) return false;
    if (seg.dealType === 'at_need' && !r.has_atneed) return false;
    if (seg.region && r.region !== seg.region) return false;
    if (seg.lang && normalizeLang(r.language) !== seg.lang) return false;
    if (filters.source !== 'all' && !wonTotalSources(r).some((ch) => channelMatchesSource(ch, filters.source))) return false;
    if (applyTimeline && filters.timeline !== 'all' && !wonTotalTimelines(r).includes(filters.timeline)) return false;
    return true;
};

type PendingRow = { region: string; deal_type: string; language: string; source_channel: string; timeline: string | null; pending: number };
type LangRow = { deal_type: string; region: string; language: string; source_channel?: string; timeline: string | null; qualified: number; won: number; lost: number };

/** The RPC results the Funnel Summary is built from (current and previous month). */
export interface FunnelRowInputs {
    qualifiedByRegion?: any[] | null;
    wonByRegion?: any[] | null;
    lostByRegion?: any[] | null;
    pendingByRegion?: PendingRow[] | null;
    prevPendingByRegion?: PendingRow[] | null;
    prevQualifiedByRegion?: any[] | null;
    prevWonByRegion?: any[] | null;
    funnelByLanguage?: LangRow[] | null;
    prevFunnelByLanguage?: LangRow[] | null;
    wonTotalRows?: WonTotalRow[] | null;
    prevWonTotalRows?: WonTotalRow[] | null;
}

export interface FunnelRow {
    label: string;
    segment: FunnelSegment;
    qualified: number;
    won: number;
    lost: number;
    pending: number;
    open: number;
    rate: string;
    isTotal: boolean;
    prevQ: number;
    prevW: number;
    prevP: number;
    wonTotal: number;
    prevWonTotal: number;
}

/**
 * Build the Funnel Summary rows: at-need QC, at-need ON, pre-need QC, pre-need
 * ON, their totals and the Grand Total. `prevClamp` caps the previous month's
 * Won Total to a day of month, for apples-to-apples deltas on a partial month.
 */
export const buildFunnelRows = (d: FunnelRowInputs, filters: PageFilters, prevClamp?: number): FunnelRow[] => {
    const rowOk = (r: any) => passesPageFilters(r, filters);
    const qbr = d.qualifiedByRegion || [];
    const wbr = d.wonByRegion || [];
    const pbr = d.pendingByRegion || [];
    const prevPbr = d.prevPendingByRegion || [];
    // Lost rows come from the timeline-aware RPC (recap's closed_lost has no
    // timeline column). Summed across buckets it equals recap closed_lost.
    const lbr = d.lostByRegion || [];

    const getQ = (dt: string, reg: string) =>
        qbr.filter((r: any) => r.deal_type === dt && r.region === reg && rowOk(r))
            .reduce((s: number, r: any) => s + (Number(r.qualified) || 0), 0);
    const getW = (dt: string, reg: string) =>
        wbr.filter((r: any) => r.deal_type === dt && r.region === reg && rowOk(r))
            .reduce((s: number, r: any) => s + (Number(r.won) || 0), 0);
    const getL = (dt: string, reg: string) =>
        lbr.filter((r: any) => r.deal_type === dt && r.region === reg && rowOk(r))
            .reduce((s: number, r: any) => s + (Number(r.count) || 0), 0);
    const getP = (dt: string, reg: string | null, lang?: string) =>
        pbr.filter((r) =>
            r.deal_type === dt
            && (!reg || r.region === reg)
            && (!lang || normalizeLang(r.language) === lang)
            && rowOk(r)
        ).reduce((s, r) => s + (Number(r.pending) || 0), 0);

    // Previous month data for deltas
    const prevQbr = d.prevQualifiedByRegion || [];
    const prevWbr = d.prevWonByRegion || [];
    const getPrevQ = (dt: string, reg: string) =>
        prevQbr.filter((r: any) => r.deal_type === dt && r.region === reg && rowOk(r))
            .reduce((s: number, r: any) => s + (Number(r.qualified) || 0), 0);
    const getPrevW = (dt: string, reg: string) =>
        prevWbr.filter((r: any) => r.deal_type === dt && r.region === reg && rowOk(r))
            .reduce((s: number, r: any) => s + (Number(r.won) || 0), 0);
    const getPrevP = (dt: string, reg: string | null, lang?: string) =>
        prevPbr.filter((r) =>
            r.deal_type === dt
            && (!reg || r.region === reg)
            && (!lang || normalizeLang(r.language) === lang)
            && rowOk(r)
        ).reduce((s, r) => s + (Number(r.pending) || 0), 0);

    // Won Total (no lead attribution): count funerals with contracts_signed_at
    // in window, by the segment and the Source filter. `clampDay` clamps to
    // day-of-month for the previous-month column on a partial month.
    const countWonTotal = (rows: WonTotalRow[] | null | undefined, seg: FunnelSegment, clampDay?: number) => {
        if (!rows) return 0;
        return rows.filter((r) => {
            if (!wonTotalInSegment(r, seg, filters)) return false;
            if (clampDay && new Date(r.contracts_signed_at).getDate() > clampDay) return false;
            return true;
        }).length;
    };

    const makeRow = (label: string, dt: DealType, reg: Region | null): FunnelRow => {
        const segment: FunnelSegment = { dealType: dt, region: reg, lang: null };
        const q = reg ? getQ(dt, reg) : qbr.filter((r: any) => r.deal_type === dt && rowOk(r)).reduce((s: number, r: any) => s + (Number(r.qualified) || 0), 0);
        const w = reg ? getW(dt, reg) : wbr.filter((r: any) => r.deal_type === dt && rowOk(r)).reduce((s: number, r: any) => s + (Number(r.won) || 0), 0);
        const l = reg ? getL(dt, reg) : lbr.filter((r: any) => r.deal_type === dt && rowOk(r)).reduce((s: number, r: any) => s + (Number(r.count) || 0), 0);
        const p = getP(dt, reg);
        const pq = reg ? getPrevQ(dt, reg) : prevQbr.filter((r: any) => r.deal_type === dt && rowOk(r)).reduce((s: number, r: any) => s + (Number(r.qualified) || 0), 0);
        const pw = reg ? getPrevW(dt, reg) : prevWbr.filter((r: any) => r.deal_type === dt && rowOk(r)).reduce((s: number, r: any) => s + (Number(r.won) || 0), 0);
        const pp = getPrevP(dt, reg);
        const wTotal = countWonTotal(d.wonTotalRows, segment);
        const pwTotal = countWonTotal(d.prevWonTotalRows, segment, prevClamp);
        const rate = q > 0 ? ((w / q) * 100).toFixed(0) : '-';
        return { label, segment, qualified: q, won: w, lost: l, pending: p, open: q - w - l, rate, isTotal: !reg, prevQ: pq, prevW: pw, prevP: pp, wonTotal: wTotal, prevWonTotal: pwTotal };
    };

    // Language-specific rows from the timeline-aware funnel-by-language RPC
    // (mirrors recap's language_by_region plus a timeline column).
    const langByRegion = d.funnelByLanguage || [];
    const prevLangByRegion = d.prevFunnelByLanguage || [];
    const makeLangRow = (label: string, dt: DealType, reg: Region, lang: 'French' | 'English'): FunnelRow => {
        const segment: FunnelSegment = { dealType: dt, region: reg, lang };
        const matching = langByRegion.filter((r: any) => r.deal_type === dt && r.region === reg && normalizeLang(r.language) === lang && rowOk(r));
        const q = matching.reduce((s: number, r: any) => s + (Number(r.qualified) || 0), 0);
        const w = matching.reduce((s: number, r: any) => s + (Number(r.won) || 0), 0);
        const l = matching.reduce((s: number, r: any) => s + (Number(r.lost) || 0), 0);
        const p = getP(dt, reg, lang);
        const prevMatching = prevLangByRegion.filter((r: any) => r.deal_type === dt && r.region === reg && normalizeLang(r.language) === lang && rowOk(r));
        const pq = prevMatching.reduce((s: number, r: any) => s + (Number(r.qualified) || 0), 0);
        const pw = prevMatching.reduce((s: number, r: any) => s + (Number(r.won) || 0), 0);
        const pp = getPrevP(dt, reg, lang);
        const wTotal = countWonTotal(d.wonTotalRows, segment);
        const pwTotal = countWonTotal(d.prevWonTotalRows, segment, prevClamp);
        const rate = q > 0 ? ((w / q) * 100).toFixed(0) : '-';
        return { label, segment, qualified: q, won: w, lost: l, pending: p, open: q - w - l, rate, isTotal: false, prevQ: pq, prevW: pw, prevP: pp, wonTotal: wTotal, prevWonTotal: pwTotal };
    };

    const rows = [
        makeLangRow('  At-Need Quebec French', 'at_need', 'quebec', 'French'),
        makeLangRow('  At-Need Quebec English', 'at_need', 'quebec', 'English'),
        makeRow('At-Need Ontario', 'at_need', 'ontario'),
        makeRow('At-Need Total', 'at_need', null),
        makeLangRow('  Pre-Need Quebec French', 'pre_need', 'quebec', 'French'),
        makeLangRow('  Pre-Need Quebec English', 'pre_need', 'quebec', 'English'),
        makeRow('Pre-Need Ontario', 'pre_need', 'ontario'),
        makeRow('Pre-Need Total', 'pre_need', null),
    ];

    // Grand total
    const totalQ = rows.filter(r => r.isTotal).reduce((s, r) => s + r.qualified, 0);
    const totalW = rows.filter(r => r.isTotal).reduce((s, r) => s + r.won, 0);
    const totalL = rows.filter(r => r.isTotal).reduce((s, r) => s + r.lost, 0);
    const totalP = (pbr || []).filter((r) => rowOk(r)).reduce((s, r) => s + (Number(r.pending) || 0), 0);
    const totalPrevQ = rows.filter(r => r.isTotal).reduce((s, r) => s + (r.prevQ || 0), 0);
    const totalPrevW = rows.filter(r => r.isTotal).reduce((s, r) => s + (r.prevW || 0), 0);
    const totalPrevP = (prevPbr || []).filter((r) => rowOk(r)).reduce((s, r) => s + (Number(r.pending) || 0), 0);
    const totalWonTotal = countWonTotal(d.wonTotalRows, GRAND_TOTAL);
    const totalPrevWonTotal = countWonTotal(d.prevWonTotalRows, GRAND_TOTAL, prevClamp);
    rows.push({
        label: 'Grand Total',
        segment: GRAND_TOTAL,
        qualified: totalQ,
        won: totalW,
        lost: totalL,
        pending: totalP,
        open: totalQ - totalW - totalL,
        rate: totalQ > 0 ? ((totalW / totalQ) * 100).toFixed(0) : '-',
        isTotal: true,
        prevQ: totalPrevQ,
        prevW: totalPrevW,
        prevP: totalPrevP,
        wonTotal: totalWonTotal,
        prevWonTotal: totalPrevWonTotal,
    });

    return rows;
};

// ---------------------------------------------------------------------------
// Row chart
// ---------------------------------------------------------------------------

/** One get_funnel_daily row. `day` is a 'YYYY-MM-DD' date. */
export interface FunnelDailyRow {
    metric: 'qualified' | 'won';
    day: string;
    deal_type: string;
    region: string;
    language: string;
    source_channel: string;
    timeline: string | null;
    n: number | string;
}

/**
 * Does a get_funnel_daily row count toward this Funnel Summary row? Region is
 * the quote's region on qualified rows and the funeral's region on won rows —
 * get_funnel_daily already emits each metric under the region the table counts
 * it by, so one comparison serves both.
 */
export const dailyRowInSegment = (r: FunnelDailyRow, seg: FunnelSegment): boolean => {
    if (!inDealType(r.deal_type, seg)) return false;
    if (seg.region && r.region !== seg.region) return false;
    if (seg.lang && normalizeLang(r.language) !== seg.lang) return false;
    return true;
};

// Chart days are local dates in the business time zone (get_funnel_daily
// returns them that way). The page's windows are cut at UTC midnight, a few
// hours before local midnight, so events at the very start of a window carry
// the previous month's last local date: they are folded into day 1, which
// keeps every counted event on the axis and each line ending on its row.
const BUSINESS_TZ = 'America/Montreal';
const localDateFormat = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
});

/** A timestamp's 'YYYY-MM-DD' date in the business time zone. */
const localDate = (timestamp: string) => {
    const parts = localDateFormat.formatToParts(new Date(timestamp));
    const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    return `${part('year')}-${part('month')}-${part('day')}`;
};

/** 1-based day on a `month` ('YYYY-MM') axis of `daysInMonth` days. */
const dayOnAxis = (date: string, month: string, daysInMonth: number) => {
    const dateMonth = date.slice(0, 7);
    if (dateMonth < month) return 1;
    if (dateMonth > month) return daysInMonth;
    return Math.min(Math.max(Number(date.slice(8, 10)), 1), daysInMonth);
};

const toCumulative = (perDay: number[]) => {
    let running = 0;
    return perDay.map((n) => (running += n));
};

/** Cumulative Qualified or Won per day of `month` for a segment, under the page filters. */
export const dailyCumulative = (
    rows: FunnelDailyRow[] | null | undefined,
    metric: FunnelDailyRow['metric'],
    seg: FunnelSegment,
    filters: PageFilters,
    month: string,
    daysInMonth: number,
): number[] => {
    const perDay = Array<number>(daysInMonth).fill(0);
    (rows || []).forEach((r) => {
        if (r.metric !== metric || !dailyRowInSegment(r, seg) || !passesPageFilters(r, filters)) return;
        perDay[dayOnAxis(r.day, month, daysInMonth) - 1] += Number(r.n) || 0;
    });
    return toCumulative(perDay);
};

/**
 * Cumulative Won Total per day of `month` for a segment. Follows both filters —
 * unlike the column, which follows Source only (see wonTotalInSegment).
 */
export const wonTotalCumulative = (
    rows: WonTotalRow[] | null | undefined,
    seg: FunnelSegment,
    filters: PageFilters,
    month: string,
    daysInMonth: number,
): number[] => {
    const perDay = Array<number>(daysInMonth).fill(0);
    (rows || []).forEach((r) => {
        if (!wonTotalInSegment(r, seg, filters, true)) return;
        perDay[dayOnAxis(localDate(r.contracts_signed_at), month, daysInMonth) - 1] += 1;
    });
    return toCumulative(perDay);
};
